import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { CalendarDaysIcon, ClockIcon, UserGroupIcon, MapPinIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import authService from '../../services/authService';
import { buildApiUrl } from '../../config/api';

interface Activity {
  id: string;
  title: string;
  type: string;
  description?: string;
  venue?: {
    id: string;
    name: string;
    address?: string;
  };
  // Support both structured venue object and flat fields for backward compatibility
  venue_id?: string;
  venue_name?: string;
  venue_address?: string;
  sessions?: Array<{
    id: string;
    date: string;
    status: string;
    sessionBlocks: Array<{
      id: string;
      startTime: string;
      endTime: string;
      isActive: boolean;
      _count: {
        bookings: number;
      };
    }>;
  }>;
}

interface Session {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  bookingsCount: number;
  activity: {
    id: string;
    title: string;
    type: string;
    venue: {
      id: string;
      name: string;
    };
  };
}

interface GeneralCreateRegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegisterCreated: () => void;
  preSelectedActivityId?: string;
}

const GeneralCreateRegisterModal: React.FC<GeneralCreateRegisterModalProps> = ({
  isOpen,
  onClose,
  onRegisterCreated,
  preSelectedActivityId
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Available fields that can be displayed on registers
  const availableFields = [
    { id: 'firstName', label: 'First Name', category: 'basic' },
    { id: 'lastName', label: 'Last Name', category: 'basic' },
    { id: 'school', label: 'School', category: 'basic' },
    { id: 'class', label: 'Class', category: 'basic' },
    { id: 'yearGroup', label: 'Year Group', category: 'basic' },
    { id: 'dateOfBirth', label: 'Date of Birth', category: 'basic' },
    { id: 'consentToWalkHome', label: 'Walking Home Consent', category: 'permissions' },
    { id: 'consentToPhotography', label: 'Photo Consent', category: 'permissions' },
    { id: 'consentToFirstAid', label: 'First Aid Consent', category: 'permissions' },
    { id: 'consentToEmergencyContact', label: 'Emergency Contact Consent', category: 'permissions' },
    { id: 'allergies', label: 'Allergies', category: 'medical' },
    { id: 'medicalInfo', label: 'Medical Info', category: 'medical' }
  ];

  const [formData, setFormData] = useState({
    activityId: '',
    sessionId: '',
    date: '',
    status: 'upcoming',
    notes: '',
    displayFields: ['firstName'] as string[] // Default to first name
  });

  useEffect(() => {
    if (isOpen) {
      fetchActivities();
      // If activity is pre-selected, set it in form
      if (preSelectedActivityId) {
        setFormData(prev => ({ ...prev, activityId: preSelectedActivityId }));
      }
    }
  }, [isOpen, preSelectedActivityId]);

  // Fetch sessions when activity is selected
  useEffect(() => {
    if (formData.activityId) {
      fetchSessionsForActivity(formData.activityId);
    } else {
      setSessions([]);
    }
  }, [formData.activityId]);

  // Fetch sessions for the selected activity
  const fetchSessionsForActivity = async (activityId: string) => {
    try {
      setLoadingSessions(true);
      const token = authService.getToken();
      if (!token) {
        return;
      }

      const response = await fetch(buildApiUrl(`/business/sessions?activityId=${activityId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sessions');
      }

      const data = await response.json();
      if (data.success) {
        // Transform sessions from API response
        const transformedSessions: Session[] = [];
        
        const sessionsData = data.data || [];
        
        sessionsData.forEach((session: any) => {
          // If session has sessionBlocks, create a session for each block
          if (session.sessionBlocks && session.sessionBlocks.length > 0) {
            session.sessionBlocks.forEach((block: any) => {
              // Ensure times are properly formatted strings
              const blockStartTime = block.startTime || session.startTime || '';
              const blockEndTime = block.endTime || session.endTime || '';
              
              transformedSessions.push({
                id: block.id || session.id, // Use block ID if available, otherwise session ID
                date: session.date,
                startTime: blockStartTime,
                endTime: blockEndTime,
                capacity: block.capacity || session.capacity || 20,
                bookingsCount: block.bookingsCount || session.bookingsCount || 0,
                activity: {
                  id: session.activity?.id || '',
                  title: session.activity?.title || '',
                  type: session.activity?.type || '',
                  venue: session.activity?.venue || { id: '', name: 'No venue' }
                }
              });
            });
          } else {
            // If no session blocks, use the session itself
            // Ensure times are properly formatted strings
            const sessionStartTime = session.startTime || '';
            const sessionEndTime = session.endTime || '';
            
            transformedSessions.push({
              id: session.id,
              date: session.date,
              startTime: sessionStartTime,
              endTime: sessionEndTime,
              capacity: session.capacity || 20,
              bookingsCount: session.bookingsCount || 0,
              activity: {
                id: session.activity?.id || '',
                title: session.activity?.title || '',
                type: session.activity?.type || '',
                venue: session.activity?.venue || { id: '', name: 'No venue' }
              }
            });
          }
        });
        
        // Debug: Log first session times to see what we're getting
        if (transformedSessions.length > 0) {
          console.log('First session time data:', {
            rawStartTime: sessionsData[0]?.startTime,
            rawEndTime: sessionsData[0]?.endTime,
            formattedStartTime: formatTime(transformedSessions[0].startTime),
            formattedEndTime: formatTime(transformedSessions[0].endTime)
          });
        }
        
        setSessions(transformedSessions);
      } else {
        throw new Error(data.message || 'Failed to fetch sessions');
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      setSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const token = authService.getToken();
      if (!token) {
        toast.error('Please log in to create registers');
        return;
      }

      const response = await fetch(buildApiUrl('/activities'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch activities');
      }

      const data = await response.json();
      if (data.success) {
        // Map API response to match component's Activity interface
        const mappedActivities = (data.data || []).map((activity: any) => ({
          ...activity,
          // Handle different API response formats
          venue: activity.venue || (activity.venue_name ? {
            id: activity.venue_id || activity.venueId || '',
            name: activity.venue_name || activity.venueName || '',
            address: activity.venue_address || activity.venueAddress
          } : undefined)
        }));
        setActivities(mappedActivities);
      } else {
        throw new Error(data.message || 'Failed to fetch activities');
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      // Reset sessionId when activity changes
      if (field === 'activityId') {
        updated.sessionId = '';
      }
      return updated;
    });
  };

  const handleFieldToggle = (fieldId: string) => {
    setFormData(prev => {
      const currentFields = prev.displayFields;
      if (currentFields.includes(fieldId)) {
        // Remove field if already selected
        return {
          ...prev,
          displayFields: currentFields.filter(f => f !== fieldId)
        };
      } else {
        // Add field if not selected
        return {
          ...prev,
          displayFields: [...currentFields, fieldId]
        };
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.activityId || !formData.sessionId || !formData.date) {
      toast.error('Please select an activity, session, and date');
      return;
    }
    
    if (formData.displayFields.length === 0) {
      toast.error('Please select at least one field to display');
      return;
    }

    try {
      setSubmitting(true);
      const token = authService.getToken();
      if (!token) {
        toast.error('Please log in to create registers');
        return;
      }

      const response = await fetch(buildApiUrl('/business/registers'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: formData.sessionId,
          date: formData.date,
          status: formData.status,
          notes: formData.notes || undefined,
          displayFields: formData.displayFields
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create register');
      }

      const data = await response.json();
      if (data.success) {
        toast.success('Register created successfully!');
        onRegisterCreated();
        onClose();
        // Reset form
        setFormData({
          activityId: '',
          sessionId: '',
          date: '',
          status: 'upcoming',
          notes: '',
          displayFields: ['firstName']
        });
      } else {
        throw new Error(data.message || 'Failed to create register');
      }
    } catch (error) {
      console.error('Error creating register:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to create register');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (time: string | null | undefined) => {
    if (!time) return '';
    
    const timeStr = String(time).trim();
    
    // PRIMARY: Handle string time in HH:MM or HH:MM:SS format (e.g., "15:00", "15:00:00", "3:00")
    // This is the most common format from the database
    const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1], 10);
      const minutes = timeMatch[2];
      // Ensure hours are in 24-hour format (0-23) with leading zero
      const paddedHours = String(hours).padStart(2, '0');
      return `${paddedHours}:${minutes}`;
    }
    
    // If it's just numbers (e.g., "1500"), try to parse as HHMM
    if (/^\d{3,4}$/.test(timeStr)) {
      const padded = timeStr.padStart(4, '0');
      const hours = padded.substring(0, 2);
      const minutes = padded.substring(2, 4);
      return `${hours}:${minutes}`;
    }
    
    // LAST RESORT: If time contains 'T' (ISO date-time) or is a Date string, try to parse it
    // NOTE: We avoid using Date objects when possible to prevent timezone issues
    if (timeStr.includes('T') || (timeStr.includes(' ') && timeStr.includes('-'))) {
      try {
        // Extract time part from ISO string if present (e.g., "2000-01-01T15:00:00Z" -> "15:00")
        const isoTimeMatch = timeStr.match(/T(\d{1,2}):(\d{2})(?::\d{2})?/);
        if (isoTimeMatch) {
          const hours = parseInt(isoTimeMatch[1], 10);
          const minutes = isoTimeMatch[2];
          return `${String(hours).padStart(2, '0')}:${minutes}`;
        }
        
        // If no T found, try parsing as full date
        const date = new Date(timeStr);
        if (!isNaN(date.getTime())) {
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${hours}:${minutes}`;
        }
      } catch (e) {
        // Fall through to return original
      }
    }
    
    // Return original if we can't parse it
    return timeStr;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create Register">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Activity Selection */}
        <div>
          <label htmlFor="activityId" className="block text-sm font-medium text-gray-700 mb-2">
            Select Activity *
          </label>
          {loading ? (
            <div className="p-3 border border-gray-300 rounded-md bg-gray-50">
              <div className="animate-pulse text-sm text-gray-500">Loading activities...</div>
            </div>
          ) : activities.length === 0 ? (
            <div className="p-3 border border-gray-300 rounded-md bg-gray-50">
              <div className="text-sm text-gray-500">No activities available</div>
            </div>
          ) : (
            <Select
              id="activityId"
              value={formData.activityId}
              onChange={(e) => handleInputChange('activityId', e.target.value)}
              required
            >
              <option value="">Choose an activity...</option>
              {activities.map((activity) => {
                const venueName = activity.venue?.name || activity.venue_name || 'No venue';
                return (
                  <option key={activity.id} value={activity.id}>
                    {activity.title} - {venueName}
                  </option>
                );
              })}
            </Select>
          )}
        </div>

        {/* Session Selection */}
        <div>
          <label htmlFor="sessionId" className="block text-sm font-medium text-gray-700 mb-2">
            Select Session *
          </label>
          {!formData.activityId ? (
            <div className="p-3 border border-gray-300 rounded-md bg-gray-50">
              <div className="text-sm text-gray-500">Please select an activity first</div>
            </div>
          ) : loadingSessions ? (
            <div className="p-3 border border-gray-300 rounded-md bg-gray-50">
              <div className="animate-pulse text-sm text-gray-500">Loading sessions...</div>
            </div>
          ) : sessions.length === 0 ? (
            <div className="p-3 border border-gray-300 rounded-md bg-gray-50">
              <div className="text-sm text-gray-500">No sessions available for this activity</div>
            </div>
          ) : (
            <Select
              id="sessionId"
              value={formData.sessionId}
              onChange={(e) => handleInputChange('sessionId', e.target.value)}
              required
            >
              <option value="">Choose a session...</option>
              {sessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {formatDate(session.date)} - {formatTime(session.startTime)} to {formatTime(session.endTime)} ({session.bookingsCount}/{session.capacity} booked)
                </option>
              ))}
            </Select>
          )}
        </div>

        {/* Date */}
        <div>
          <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-2">
            Register Date *
          </label>
          <Input
            id="date"
            type="date"
            value={formData.date}
            onChange={(e) => handleInputChange('date', e.target.value)}
            required
          />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
            Status
          </label>
          <Select
            id="status"
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="upcoming">Upcoming</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </div>

        {/* Display Fields Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Fields to Display *
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Choose which details should appear on this register
          </p>
          <div className="border border-gray-300 rounded-md p-4 bg-gray-50 max-h-64 overflow-y-auto">
            <div className="space-y-3">
              {/* Basic Fields */}
              <div>
                <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Basic Information</h5>
                <div className="grid grid-cols-2 gap-2">
                  {availableFields.filter(f => f.category === 'basic').map(field => (
                    <label key={field.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.displayFields.includes(field.id)}
                        onChange={() => handleFieldToggle(field.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Permissions */}
              <div>
                <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase mt-4">Permissions</h5>
                <div className="grid grid-cols-2 gap-2">
                  {availableFields.filter(f => f.category === 'permissions').map(field => (
                    <label key={field.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.displayFields.includes(field.id)}
                        onChange={() => handleFieldToggle(field.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Medical */}
              <div>
                <h5 className="text-xs font-semibold text-gray-700 mb-2 uppercase mt-4">Medical</h5>
                <div className="grid grid-cols-2 gap-2">
                  {availableFields.filter(f => f.category === 'medical').map(field => (
                    <label key={field.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.displayFields.includes(field.id)}
                        onChange={() => handleFieldToggle(field.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{field.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          {formData.displayFields.length === 0 && (
            <p className="text-xs text-red-600 mt-1">At least one field must be selected</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            Notes (Optional)
          </label>
          <Textarea
            id="notes"
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            rows={3}
            placeholder="Add any notes about this register..."
          />
        </div>

        {/* Selected Activity/Session Info */}
        {formData.activityId && formData.sessionId && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Register Details</h4>
            {(() => {
              const selectedActivity = activities.find(a => a.id === formData.activityId);
              const selectedSession = sessions.find(s => s.id === formData.sessionId);
              if (!selectedActivity || !selectedSession) return null;
              
              return (
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <CalendarDaysIcon className="h-4 w-4 mr-2" />
                    <span><strong>{selectedActivity.title}</strong></span>
                  </div>
                  <div className="flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    <span>{selectedActivity.venue?.name || 'No venue'}</span>
                  </div>
                  <div className="flex items-center">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    <span>{formatDate(selectedSession.date)} - {formatTime(selectedSession.startTime)} to {formatTime(selectedSession.endTime)}</span>
                  </div>
                  <div className="flex items-center">
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    <span>{selectedSession.bookingsCount} / {selectedSession.capacity} attendees</span>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting || !formData.activityId || !formData.sessionId || !formData.date || formData.displayFields.length === 0}
            className="flex-1"
          >
            {submitting ? 'Creating...' : 'Create Register'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default GeneralCreateRegisterModal;
